import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import type { DemoLanguage, StageId } from '@/constants/content'

import {
    ArrowRight,
    Mic,
    Pause,
    Play,
    Sparkles,
    Volume2,
    VolumeX,
    X
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { BuckyMascot } from '@/components/generals/bucky-mascot'

import { demoLanguages, demoScript } from '@/constants/content'

export interface Bubble {
    id: number
    x: number
    y: number
    size: number
    delay: number
}

export const defaultBubbles: Bubble[] = [
    { id: 1, x: 14, y: 58, size: 9, delay: 0 },
    { id: 2, x: 30, y: 34, size: 7, delay: 0.6 },
    { id: 3, x: 46, y: 62, size: 10, delay: 1.2 },
    { id: 4, x: 62, y: 38, size: 8, delay: 0.3 },
    { id: 5, x: 76, y: 60, size: 7, delay: 0.9 }
]

interface ConceptScreenProps {
    stage: StageId
    language: DemoLanguage
    spokenLanguage?: DemoLanguage
    score: number
    bubbles?: Bubble[]
    interactive?: boolean
    speaking?: boolean
    muted?: boolean
    selectedQuestion?: string | null
    subtitleOverride?: string
    nextStepOverride?: string
    gameIcon?: LucideIcon
    className?: string
    onPlay?: () => void
    onPop?: (id: number) => void
    onTapBucky?: () => void
    onClose?: () => void
    onSelectQuestion?: (id: string) => void
    onShowNextStep?: () => void
    onBackToGame?: () => void
    onToggleMute?: () => void
}

const shortLabel = (language: DemoLanguage): string => demoLanguages.find(item => item.id === language)?.short ?? 'EN'

// The Play-to-Pitch concept screen. A drawn illustration of the planned experience, not a product screenshot.
export const ConceptScreen: FC<ConceptScreenProps> = ({
    stage,
    language,
    spokenLanguage = 'en',
    score,
    bubbles = defaultBubbles,
    interactive = false,
    speaking = false,
    muted = false,
    selectedQuestion = null,
    subtitleOverride,
    nextStepOverride,
    gameIcon: GameIcon,
    className,
    onPlay,
    onPop,
    onTapBucky,
    onClose,
    onSelectQuestion,
    onShowNextStep,
    onBackToGame,
    onToggleMute
}): ReactNode => {
    const t = (entry: { en: string, ms: string }): string => entry[language]
    const showGame = stage !== 'connect'
    const gamePaused = stage === 'discover'
    const answer = demoScript.questions.find(item => item.id === selectedQuestion)

    const subtitle = subtitleOverride
        ?? (stage === 'engage' ? t(demoScript.engage) : stage === 'connect' ? t(demoScript.connect) : null)

    const Interactive = interactive ? 'button' : 'span'

    return (
        <div
            className={cn(
                'relative aspect-[4/3] w-full overflow-hidden rounded-[10px] bg-navy-950 text-soft-white select-none @lg:aspect-[16/10] @lg:rounded-[14px]',
                className
            )}
        >
            <div
                aria-hidden
                className='absolute inset-0 bg-[radial-gradient(70%_60%_at_50%_45%,rgb(49_82_122/0.55),transparent_75%),radial-gradient(circle_at_center,rgb(244_247_250/0.07)_1px,transparent_1.5px)] bg-size-[100%_100%,18px_18px]'
            />

            <div
                className={cn(
                    'absolute inset-0 transition duration-500 ease-out motion-reduce:transition-none',
                    gamePaused && 'scale-[0.94] opacity-40 blur-[2px]',
                    !showGame && 'opacity-25 blur-[3px]'
                )}
            >
                <div className='absolute top-3 left-3 flex items-center gap-1.5 rounded-full bg-navy-900/80 px-2.5 py-1 text-[10px] font-semibold text-soft-white ring-1 ring-soft-white/10 @lg:top-4 @lg:left-4 @lg:text-xs'>
                    <Sparkles className='size-3 text-orange @lg:size-3.5' aria-hidden />
                    {t(demoScript.score)} {score}
                </div>

                <span className='absolute bottom-3 left-3 hidden rounded-full bg-navy-900/80 px-2 py-1 text-[10px] font-medium text-cool-grey-light ring-1 ring-soft-white/10 @md:inline-flex @lg:bottom-4 @lg:left-4'>
                    Voice {shortLabel(spokenLanguage)} · Subtitles {shortLabel(language)}
                </span>

                <div className='absolute top-3 right-3 @lg:top-4 @lg:right-4'>
                    <Interactive
                        {...(interactive ? { type: 'button' as const, onClick: onToggleMute, 'aria-pressed': muted, 'aria-label': muted ? 'Unmute Bucky (illustration, no audio plays)' : 'Mute Bucky (illustration, no audio plays)' } : { 'aria-hidden': true })}
                        className={cn(
                            'grid size-7 place-items-center rounded-full bg-navy-900/80 ring-1 ring-soft-white/10 @lg:size-8',
                            interactive && 'transition hover:bg-navy-700'
                        )}
                    >
                        {muted ? <VolumeX className='size-3.5' aria-hidden /> : <Volume2 className='size-3.5' aria-hidden />}
                    </Interactive>
                </div>

                {bubbles.map(bubble => {
                    const style = {
                        left: `${bubble.x}%`,
                        top: `${bubble.y}%`,
                        width: `${bubble.size}%`,
                        animationDelay: `${bubble.delay}s`
                    }

                    const content = GameIcon
                        ? <GameIcon className='size-1/2 text-navy' aria-hidden />
                        : <span className='absolute top-[18%] left-[22%] size-[28%] rounded-full bg-soft-white/70' aria-hidden />

                    if (interactive && stage === 'engage') {
                        return (
                            <button
                                key={bubble.id}
                                type='button'
                                onClick={() => onPop?.(bubble.id)}
                                aria-label='Pop bubble, plus 10 points'
                                style={style}
                                className='bubble-float absolute grid aspect-square -translate-x-1/2 -translate-y-1/2 cursor-pointer place-items-center rounded-full bg-[radial-gradient(circle_at_35%_30%,#FFB37F,#FF8A3D_55%,#E5722A)] shadow-[0_6px_18px_-6px_rgb(255_138_61/0.8)] transition hover:scale-110 active:scale-90'
                            >
                                {content}
                            </button>
                        )
                    }

                    return (
                        <span
                            key={bubble.id}
                            aria-hidden
                            style={style}
                            className='bubble-float absolute grid aspect-square -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-[radial-gradient(circle_at_35%_30%,#FFB37F,#FF8A3D_55%,#E5722A)] shadow-[0_6px_18px_-6px_rgb(255_138_61/0.8)]'
                        >
                            {content}
                        </span>
                    )
                })}

                {stage === 'attract' && (
                    <div className='absolute inset-0 grid place-items-center bg-navy-950/45'>
                        <div className='flex flex-col items-center gap-2 rounded-2xl bg-navy-900/90 px-5 py-4 text-center ring-1 ring-soft-white/10 @lg:gap-3 @lg:px-8 @lg:py-6'>
                            <p className='font-heading text-sm font-bold @lg:text-xl'>
                                {t(demoScript.gameTitle)}
                            </p>

                            <p className='max-w-[18rem] text-[10px] text-cool-grey-light @lg:text-xs'>
                                {t(demoScript.attract)}
                            </p>

                            <Interactive
                                {...(interactive ? { type: 'button' as const, onClick: onPlay } : { 'aria-hidden': true })}
                                className={cn(
                                    'inline-flex items-center gap-1.5 rounded-full bg-orange px-4 py-1.5 text-xs font-bold text-navy @lg:text-sm',
                                    interactive && 'transition hover:bg-orange-light active:scale-95'
                                )}
                            >
                                <Play className='size-3.5' aria-hidden />
                                {t(demoScript.tapToPlay)}
                            </Interactive>
                        </div>
                    </div>
                )}
            </div>

            {subtitle && stage !== 'discover' && (
                <div className='absolute top-11 left-1/2 w-[78%] -translate-x-1/2 @lg:top-4 @lg:w-[52%]'>
                    <p
                        aria-live={interactive ? 'polite' : undefined}
                        className='line-clamp-2 rounded-lg bg-navy-950/75 px-3 py-1.5 text-center text-[10px] leading-snug font-medium text-soft-white ring-1 ring-soft-white/10 backdrop-blur-sm @lg:text-[13px]'
                    >
                        {subtitle}
                    </p>
                </div>
            )}

            {stage !== 'discover' && (
                <div className='absolute right-3 bottom-3 flex items-center gap-2 @lg:right-4 @lg:bottom-4'>
                    {speaking && !muted && (
                        <span aria-hidden className='flex h-4 items-end gap-0.5'>
                            <span className='sound-bar h-full w-0.5 rounded-full bg-orange' />
                            <span className='sound-bar h-full w-0.5 rounded-full bg-orange [animation-delay:0.15s]' />
                            <span className='sound-bar h-full w-0.5 rounded-full bg-orange [animation-delay:0.3s]' />
                        </span>
                    )}

                    <Interactive
                        {...(interactive ? { type: 'button' as const, onClick: onTapBucky, 'aria-label': 'Tap Bucky to open the conversation' } : { 'aria-hidden': true })}
                        className={cn(
                            'block size-10 rounded-full bg-navy-900 p-0.5 ring-2 ring-orange/70 @lg:size-14',
                            speaking && 'bucky-speaking',
                            interactive && 'cursor-pointer transition hover:scale-105 active:scale-95'
                        )}
                    >
                        <BuckyMascot size={56} decorative className='size-full' />
                    </Interactive>
                </div>
            )}

            {stage === 'discover' && (
                <div className='absolute inset-2 flex flex-col overflow-hidden rounded-xl bg-navy-900/95 ring-1 ring-soft-white/15 animate-in fade-in slide-in-from-right-4 duration-300 @lg:inset-y-3 @lg:right-3 @lg:left-auto @lg:w-[50%]'>
                    <div className='flex items-center gap-2 border-b border-soft-white/10 px-3 py-2'>
                        <span className='block size-7 shrink-0 rounded-full bg-navy-800 p-0.5 ring-2 ring-orange/70'>
                            <BuckyMascot size={28} decorative className='size-full' />
                        </span>

                        <div className='min-w-0 flex-1 leading-tight'>
                            <p className='text-xs font-semibold'>
                                Bucky
                            </p>

                            <p className='truncate text-[10px] text-cool-grey-light'>
                                {t(demoScript.sampleOnly)}
                            </p>
                        </div>

                        <span className='inline-flex items-center gap-1 rounded-full bg-navy-800 px-2 py-0.5 text-[10px] text-cool-grey-light'>
                            <Pause className='size-3' aria-hidden />
                            {t(demoScript.paused)}
                        </span>

                        <Interactive
                            {...(interactive ? { type: 'button' as const, onClick: onClose, 'aria-label': 'Close the conversation and return to the game' } : { 'aria-hidden': true })}
                            className={cn(
                                'grid size-8 shrink-0 place-items-center rounded-full bg-navy-800',
                                interactive && 'transition hover:bg-navy-700'
                            )}
                        >
                            <X className='size-4' aria-hidden />
                        </Interactive>
                    </div>

                    <div className='flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-3 py-2.5 text-[11px] leading-snug @lg:text-xs'>
                        <p className='max-w-[90%] rounded-xl rounded-tl-sm bg-navy-700 px-3 py-2'>
                            {t(demoScript.greeting)}
                        </p>

                        <div className='flex flex-wrap gap-1.5'>
                            {demoScript.questions.map(item => (
                                <Interactive
                                    key={item.id}
                                    {...(interactive ? { type: 'button' as const, onClick: () => onSelectQuestion?.(item.id), 'aria-pressed': selectedQuestion === item.id } : { 'aria-hidden': true })}
                                    className={cn(
                                        'rounded-full border px-2.5 py-1 text-left text-[10px] @lg:text-[11px]',
                                        selectedQuestion === item.id
                                            ? 'border-orange bg-orange/15 text-soft-white'
                                            : 'border-soft-white/15 text-cool-grey-light',
                                        interactive && 'transition hover:border-orange/60 hover:text-soft-white'
                                    )}
                                >
                                    {t(item.question)}
                                </Interactive>
                            ))}
                        </div>

                        {answer && (
                            <>
                                <p className='ml-auto max-w-[85%] rounded-xl rounded-tr-sm bg-orange/90 px-3 py-2 text-navy'>
                                    {t(answer.question)}
                                </p>

                                <p className='max-w-[90%] rounded-xl rounded-tl-sm bg-navy-700 px-3 py-2 animate-in fade-in duration-300'>
                                    {t(answer.answer)}
                                </p>
                            </>
                        )}
                    </div>

                    <div className='flex items-center gap-2 border-t border-soft-white/10 px-3 py-2'>
                        <span aria-hidden className='grid size-8 shrink-0 place-items-center rounded-full bg-navy-700 text-cool-grey-light'>
                            <Mic className='size-4' />
                        </span>

                        <span className='flex-1 truncate text-[10px] text-cool-grey-light'>
                            Voice shown for illustration
                        </span>

                        <Interactive
                            {...(interactive ? { type: 'button' as const, onClick: onShowNextStep } : { 'aria-hidden': true })}
                            className={cn(
                                'inline-flex items-center gap-1 rounded-full bg-orange px-3 py-1.5 text-[10px] font-bold text-navy @lg:text-[11px]',
                                interactive && 'transition hover:bg-orange-light'
                            )}
                        >
                            {t(demoScript.seeNextStep)}
                            <ArrowRight className='size-3' aria-hidden />
                        </Interactive>
                    </div>
                </div>
            )}

            {stage === 'connect' && (
                <div className='absolute inset-x-4 top-1/2 mx-auto max-w-[24rem] -translate-y-[42%] rounded-2xl bg-navy-900/95 p-3 ring-1 ring-soft-white/15 animate-in fade-in zoom-in-95 duration-300 @lg:p-5'>
                    <p className='text-[10px] font-semibold tracking-[0.14em] text-orange uppercase'>
                        {t(demoScript.nextStep)}
                    </p>

                    <ul className='mt-2 flex flex-col gap-1.5'>
                        {(nextStepOverride ? [{ en: nextStepOverride, ms: nextStepOverride }] : demoScript.nextSteps).map((step, index) => (
                            <li
                                key={step.en}
                                className={cn(
                                    'flex items-center justify-between rounded-lg px-3 py-1.5 text-[11px] @lg:text-xs',
                                    index === 0 ? 'bg-orange text-navy font-semibold' : 'bg-navy-800 text-soft-white/85'
                                )}
                            >
                                {t(step)}
                                {index === 0 && <ArrowRight className='size-3.5' aria-hidden />}
                            </li>
                        ))}
                    </ul>

                    {(interactive || onBackToGame) && (
                        <Interactive
                            {...(interactive ? { type: 'button' as const, onClick: onBackToGame } : { 'aria-hidden': true })}
                            className={cn(
                                'mt-3 inline-flex items-center gap-1.5 text-[11px] font-semibold text-cool-grey-light',
                                interactive && 'transition hover:text-soft-white'
                            )}
                        >
                            <Play className='size-3' aria-hidden />
                            {t(demoScript.backToGame)} · {t(demoScript.score)} {score}
                        </Interactive>
                    )}
                </div>
            )}
        </div>
    )
}
